/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React from 'react';
import classNames from 'classnames';
import { useDrop } from 'react-dnd';
import { X } from 'react-feather';
import { StatusCheck, StatusClose } from 'components/ui/Icon/Streamline';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import IconButton from 'ui/IconButton';
import { ItemTypes } from 'constants/reactDnDItemTypes';
import { ASSISTIVE_TEXT_STATES } from './AssistiveText';

function getMatch(choice, choices, pairs) {
  const index = choices.indexOf(choice);
  const match = pairs[index];
  const displayText = !match
    ? t('questions.choices.matching.add_match')
    : match.match;
  return displayText;
}

function addXButton(choice, choices, pairs, removeMatchBtn) {
  const index = choices.indexOf(choice);
  const match = pairs[index];
  const xButton = !match ? null : (
    <div>
      <IconButton
        type="button"
        onClick={() => removeMatchBtn(choice, pairs[index])}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            removeMatchBtn(choice, pairs[index]);
          }
        }}
        aria-label={`${t('common.remove')} match for ${choice.item}. ${
          match.match
        }.`}
        className="ItemIcon"
      >
        <X size={24} />
      </IconButton>
    </div>
  );
  return xButton;
}

function answerStatus(
  choice,
  choices,
  isShowingResults,
  pairs,
  removeMatchBtn,
) {
  if (isShowingResults) {
    const index = choices.indexOf(choice);
    const isCorrect = choices[index].correct;
    let icon;
    if (isCorrect) {
      icon = <StatusCheck className="fs-0 float-right" />;
    } else {
      icon = <StatusClose className="fs-0 float-right" />;
    }

    return <div className="ItemIcon">{icon}</div>;
  }
  return addXButton(choice, choices, pairs, removeMatchBtn);
}

const MatchContainer = ({
  children,
  addMatchToPairs,
  index,
  questionId,
  tabIndex,
}) => {
  const [, drop] = useDrop(() => ({
    accept: ItemTypes.MATCHING,
    drop: match => {
      addMatchToPairs(match, index);
    },
    canDrop: ({ questionId: matchQuestionId }) => {
      if (matchQuestionId === questionId) return true;
      return false;
    },
    collect: mon => mon,
  }));
  return (
    <div className="AddMatchContainer" ref={drop} tabIndex={tabIndex}>
      {children}
    </div>
  );
};

const ItemChoice = props => {
  const {
    questionId,
    addMatch,
    removeMatch,
    choices,
    active,
    onClickItemChoice,
    pairs,
    setPairs,
    replaceMatch,
    isShowingResults,
    setAssistiveStatus,
  } = props;

  const addMatchToPairs = (match, index) => {
    const updatedValue = {};
    updatedValue[index] = match.match;
    let hasCurrentPair;
    setPairs(prevPairs => {
      hasCurrentPair = prevPairs[index];
      return {
        ...prevPairs,
        ...updatedValue,
      };
    });
    if (hasCurrentPair) {
      replaceMatch(hasCurrentPair, match.match);
    } else {
      removeMatch(match.match);
    }
  };

  const onItemFocus = index => {
    setAssistiveStatus({
      state: ASSISTIVE_TEXT_STATES.ITEM_FOCUSED,
      item: choices[index].item,
      position: index + 1,
      total: choices.length,
    });
  };

  const removeMatchBtn = (choice, matchPieceToReAdd) => {
    const index = choices.indexOf(choice);
    const updatedValue = {};
    updatedValue[index] = null;

    // eslint-disable-next-line no-shadow
    setPairs(pairs => ({
      ...pairs,
      ...updatedValue,
    }));
    addMatch(matchPieceToReAdd);
    setAssistiveStatus({
      state: ASSISTIVE_TEXT_STATES.MATCH_REMOVED,
      item: choice.item,
    });
    onItemFocus(index);
  };

  const itemText = choice => {
    let variant;
    let className;
    if (pairs[choices.indexOf(choice)]) {
      variant = TEXT_VARIANTS.MANTLE_BODY;
      className = 'MatchingTextWithX';
    } else {
      variant = TEXT_VARIANTS.BODY_LIGHT;
      className = 'MatchingText';
    }
    return (
      <Text variant={variant} className={className}>
        {getMatch(choice, choices, pairs)}
      </Text>
    );
  };

  return (
    <div className="MatchingItem d-f fd-c">
      {choices.map((choice, index) => (
        <div
          className={classNames('PuzzleContainer pb-16', {
            PuzzleActive: index === active,
            PuzzleHasMatch: pairs[index],
          })}
          onClick={e => {
            e.preventDefault();
            onClickItemChoice(index);
          }}
          onMouseDown={e => {
            // Prevents mouse click focus ring
            e.preventDefault();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onClickItemChoice(index);
            }
          }}
          onFocus={() => {
            if (!pairs[choices.indexOf(choice)]) {
              onItemFocus(index);
            }
          }}
          key={choice.id}
        >
          <div className="d-f fd-r jc-c">
            <div className="ItemContainer">
              <div className="pl-20 pb-16 pt-16">
                <Text variant={TEXT_VARIANTS.MANTLE_BODY}>{choice.item}</Text>
              </div>
              <span className="RightPuzzle" />
            </div>
            <MatchContainer
              addMatchToPairs={addMatchToPairs}
              index={index}
              questionId={questionId}
              tabIndex={pairs[choices.indexOf(choice)] ? '-1' : '0'}
            >
              <div className="d-f fd-r jc-se ai-c">
                {itemText(choice)}
                {answerStatus(
                  choice,
                  choices,
                  isShowingResults,
                  pairs,
                  removeMatchBtn,
                )}
              </div>
            </MatchContainer>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ItemChoice;
